<template>
  <BaseDialog max-width="900" persistent :value="true">
    <template #modal>
      <BaseCosmoCard>
        <template #title>
          <div class="modal_header">
            <BaseTitle v-text="dialogTitle" />
            <BaseButtonIcon
              class="close_icon"
              icon="$mdiClose"
              :disabled="isSubmiting"
              @click="reset()"
            />
          </div>
        </template>
        <div class="modal_content">
          <v-progress-linear
            v-if="isSubmiting"
            class="progression"
            indeterminate
          />
          <!-- ACTION SECTION -->
          <div class="input_group">
            <div class="input_section">
              <div class="action_type">
                <div class="action_label">Type d'action:</div>
                <div class="action_effect">{{ effectType }}</div>
              </div>
            </div>
          </div>
          <!-- TRIGER SECTION -->
          <div class="input_group mt-6">
            <div class="input_section">
              <!-- TRIGER CONFIGURATION -->
              <div>
                <div class="input_label" v-text="$t('utils.trigger')" />
                <base-autocomplete
                  v-model="eventEffect.action"
                  :items="eventItems"
                  outlined
                  item-text="label"
                  item-value="value"
                  :disabled="isSubmiting"
                  :error-messages="eventEffectActionError"
                  :has-error="!!eventEffectActionError"
                  clearable
                  @change="handleEventChange()"
                />
              </div>
            </div>
            <div class="input_section">
              <template>
                <div
                  v-if="eventEffect.action === AUTOMATION_EVENT.STATUS_IS_SET"
                  class="action_section"
                >
                  <div class="input_label" v-text="$t('utils.destination')" />
                  <base-autocomplete
                    v-if="eventEffect.action === AUTOMATION_EVENT.STATUS_IS_SET"
                    v-model="eventEffect.content_id"
                    :items="workflowStatuses"
                    :placeholder="$t('automation.dialog.arrival_status')"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    :error-messages="eventEffectContentError"
                    :has-error="!!eventEffectContentError"
                    clearable
                  />
                </div>
                <!-- TRIGER ACTIVATE_IN -->
                <div
                  v-else-if="
                    eventEffect.action === AUTOMATION_EVENT.ACTIVATE_IN
                  "
                  class="action_section"
                >
                  <div
                    class="input_label"
                    v-text="$t('activation.delay_in_days')"
                  />
                  <BaseTextField
                    v-model="eventEffect.activate_in"
                    type="number"
                    :error-messages="activeInError"
                    :has-error="!!activeInError"
                  />
                </div>

                <!-- TRIGER DOC_GENERATED -->
                <div
                  v-else-if="
                    eventEffect.action === AUTOMATION_EVENT.DOC_GENERATED &&
                    loaded
                  "
                  class="action_section"
                >
                  <div
                    class="input_label"
                    v-text="$t('automation.choose_an_document_template')"
                  />
                  <base-autocomplete
                    v-model="eventEffect.content_id"
                    :items="eventDocumentTemplates.items"
                    :loading="eventDocumentTemplates.loading"
                    :filter="() => true"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    :search-input.sync="eventDocumentTemplates.name"
                    clearable
                  />
                </div>
                <!-- CAMPAIGN_ANSWERED  -->
                <div
                  v-else-if="
                    eventEffect.action === AUTOMATION_EVENT.CAMPAIGN_ANSWERED
                  "
                  class="action_section"
                >
                  <div
                    class="input_label"
                    v-text="$t('automation.dialog.choose_campaign')"
                  />
                  <base-autocomplete
                    v-model="eventEffect.content_id"
                    :items="eventCampaignFiltered"
                    :loading="eventCampaignFilter.loading"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    clearable
                    :error-messages="eventEffectContentError"
                    :has-error="!!eventEffectContentError"
                    :filter="() => true"
                    :search-input.sync="eventCampaignFilter.name"
                  />
                </div>

                <!-- CAMPAIGN_ANSWERED  -->
                <div
                  v-else-if="
                    eventEffect.action === AUTOMATION_EVENT.FORM_ANSWERED
                  "
                  class="action_section"
                >
                  <div
                    class="input_label"
                    v-text="$t('automation.dialog.choose_form')"
                  />

                  <base-autocomplete
                    v-if="firstInitialization"
                    v-model="eventEffect.content_id"
                    :items="eventFormsItems"
                    :loading="eventForms.loading"
                    outlined
                    item-text="title"
                    item-value="id"
                    :disabled="isSubmiting"
                    clearable
                    :error-messages="eventEffectContentError"
                    :has-error="!!eventEffectContentError"
                    :filter="() => true"
                    :search-input.sync="eventForms.name"
                    @change="handleClear"
                  />
                  <v-skeleton-loader v-else class="skeleton" type="text" />
                </div>

                <!-- WIZIDEE_CONTROL_FAILED  -->
                <div
                  v-else-if="
                    eventEffect.action ===
                    AUTOMATION_EVENT.WIZIDEE_CONTROL_FAILED
                  "
                  class="action_section"
                >
                  <div
                    class="input_label"
                    v-text="$t('automation.dialog.choose_validation_scheme')"
                  />
                  <base-autocomplete
                    v-model="eventEffect.content_id"
                    :items="validationSchema.items"
                    :loading="validationSchema.loading"
                    outlined
                    item-text="name"
                    item-value="id"
                    :disabled="isSubmiting"
                    clearable
                    :error-messages="eventEffectContentError"
                    :has-error="!!eventEffectContentError"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="modal_footer">
          <BaseButton
            :disabled="isSubmiting"
            color="grey"
            type="secondary"
            @click="reset()"
          >
            {{ $t("utils.cancel") }}
          </BaseButton>
          <BaseButton
            :disabled="isSubmiting"
            color="primary"
            type="primary"
            @click="submit()"
          >
            {{ $t("utils.validate") }}
          </BaseButton>
        </div>
      </BaseCosmoCard>
    </template>
  </BaseDialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";
import {
  getOrganizationCosmoformTemplate,
  getOrganizationWorkflowCosmoformTemplate,
  getWorkflowQuestions,
} from "@/modules/Admin/Services/workflow.service";
import { getOrganizationCampaigns } from "@/modules/Editor/Services/campaign.service";
import { AUTOMATION_EFFECT, AUTOMATION_EVENT } from "@/core/Utils/types.utils";
import OrganizationAPI from "@/core/Services/organization.api";

export default {
  name: "EffectAutomationDialog",
  mixins: [validationMixin],
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
    hasEnoxa: {
      type: Boolean,
    },
    workflow: {
      type: Object,
      required: true,
    },
    automationActionToEdit: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      openAdvancedSettings: false,
      isFetchingCampaign: true,
      isSubmiting: false,
      questionsFilesFilter: {
        loading: false,
        page: 1,
        slug: undefined,
        pageSize: 10,
        name: undefined,
        searchTimeoutRef: undefined,
        typeQuestion: ["FILE", "MULTIPLE_FILE"],
      },
      questionFiles: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      collaboratorFilter: {
        loading: false,
        page: 1,
        items: [],
        pageSize: 10,
        email: undefined,
        searchTimeoutRef: undefined,
      },
      effectItems: [
        {
          label: this.$t("automation.effect.set_status"),
          value: AUTOMATION_EFFECT.SET_STATUS,
        },
        {
          label: this.$t("automation.effect.send_offer"),
          value: AUTOMATION_EFFECT.SEND_OFFER,
        },
        {
          label: this.$t("automation.effect.send_form"),
          value: AUTOMATION_EFFECT.SEND_FORM,
        },
        {
          label: this.$t("automation.effect.send_to_ged"),
          value: AUTOMATION_EFFECT.SEND_TO_GED,
          // disabled: !this.hasEnoxa,
        },
        {
          label: this.$t("automation.effect.archive_folder"),
          value: AUTOMATION_EFFECT.ARCHIVE_FOLDER,
        },
        {
          label: this.$t("automation.effect.send_campaign"),
          value: AUTOMATION_EFFECT.SEND_CAMPAIGN,
        },
        {
          label: this.$t("automation.effect.generate_doc"),
          value: AUTOMATION_EFFECT.GENERATE_DOC,
        },
        {
          label: this.$t("automation.effect.assign_collaborator"),
          value: AUTOMATION_EFFECT.ASSIGN_COLLABORATOR,
        },
      ],
      eventItems: [
        {
          label: this.$t("automation.event_config.form_answered"),
          value: AUTOMATION_EVENT.FORM_ANSWERED,
        },
        {
          label: this.$t("automation.event_config.status_is_set"),
          value: AUTOMATION_EVENT.STATUS_IS_SET,
        },
        {
          label: this.$t("automation.event_config.create_folder"),
          value: AUTOMATION_EVENT.CREATE_FOLDER,
        },
        {
          label: this.$t("automation.event.document_generated"),
          value: AUTOMATION_EVENT.DOC_GENERATED,
        },
        {
          label: this.$t("automation.event_config.campaign_answered"),
          value: AUTOMATION_EVENT.CAMPAIGN_ANSWERED,
        },
        {
          label: this.$t("automation.event_config.activate_in"),
          value: AUTOMATION_EVENT.ACTIVATE_IN,
        },
        {
          label: this.$t("automation.event_config.wizidee_control_failed"),
          value: AUTOMATION_EVENT.WIZIDEE_CONTROL_FAILED,
        },
        {
          label: this.$t("automation.event_config.offer_signed"),
          value: AUTOMATION_EVENT.OFFER_SIGNED,
        },
        {
          label: this.$t("automation.event_config.manual"),
          value: AUTOMATION_EVENT.MANUAL,
        },
      ],
      eventEffect: {
        action: null,
        content_id: null,
        activate_in: null,
      },
      offerTemplates: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      eventCampaigns: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
      },
      firstInitialization: false,
      loaded: false,
      eventForms: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
        name: undefined,
        searchTimeoutRef: undefined,
        page: 1,
        pageSize: 100,
      },
      eventDocumentTemplates: {
        items: [],
        count: 0,
        next: 0,
        previous: 0,
        name: undefined,
        searchTimeoutRef: undefined,
        page: 1,
        pageSize: 100,
      },
      eventCampaignFilter: {
        loading: false,
        page: 1,
        pageSize: 100,
        name: undefined,
        status: "IN_PROGRESS",
        searchTimeoutRef: undefined,
      },
      statuses: [],
      fetchingOfferTemplate: false,
      fetchingCosmoformTemplate: false,
      cosmoformTemplates: [],
      fetchingDocumentTemplates: false,
      documentTemplates: [],
      AUTOMATION_EFFECT: AUTOMATION_EFFECT,
      AUTOMATION_EVENT: AUTOMATION_EVENT,
      validationSchema: {
        loading: false,
        items: [],
      },
      isInitializing: true,
    };
  },
  validations() {
    let validations = {};
    validations.eventEffect = {
      action: {
        required,
      },
      content_id: {
        required: requiredIf(
          () =>
            this.eventEffect.action == AUTOMATION_EVENT.STATUS_IS_SET ||
            this.eventEffect.action == AUTOMATION_EVENT.CAMPAIGN_ANSWERED ||
            this.eventEffect.action == AUTOMATION_EVENT.WIZIDEE_CONTROL_FAILED
        ),
      },
      activate_in: {
        required: requiredIf(
          () => this.eventEffect.action == AUTOMATION_EVENT.ACTIVATE_IN
        ),
        minValue: (value) => {
          if (this.eventEffect.action === AUTOMATION_EVENT.ACTIVATE_IN) {
            return value >= 0;
          }
          return true;
        },
      },
    };
    return validations;
  },
  computed: {
    dialogTitle() {
      const defaultTitle = this.getDefaultTitle(
        this.automationActionToEdit.action,
        this.automationActionToEdit.actionExtra
      );
      return this.$t("utils.automate", {
        action_name: this.automationActionToEdit.label || defaultTitle,
      });
    },
    workflowStatuses() {
      if (!this.workflow?.statuses) return [];
      return this.workflow.statuses;
    },
    eventCampaignFiltered() {
      return this.eventCampaigns.items;
    },
    eventEffectActionError() {
      if (!this.$v.eventEffect.action.$dirty) return "";
      if (!this.$v.eventEffect.action.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    eventEffectContentError() {
      if (!this.$v.eventEffect.content_id.$dirty) return "";
      if (!this.$v.eventEffect.content_id.required)
        return this.$t("utils.fieldIsRequired");
      return "";
    },
    activeInError() {
      if (!this.$v.eventEffect.activate_in.$dirty) return "";
      if (!this.$v.eventEffect.activate_in.required)
        return this.$t("utils.fieldIsRequired");
      if (!this.$v.eventEffect.activate_in.minValue)
        return this.$t("utils.must_be_positive_number");
      return "";
    },
    effectType() {
      return this.effectItems.find(
        (effect) => effect.value === this.automationActionToEdit.action
      )?.label;
    },
    eventFormsItems() {
      const allForms = {
        title: this.$t("folder.cosmoform.whateverForms"),
        id: "",
      };
      return [allForms, ...this.eventForms.items];
    },
  },
  watch: {
    "eventCampaignFilter.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCampaigns();
    },
    "eventDocumentTemplates.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterDocumentTemplates();
    },
    "eventForms.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCosmoformTemplate();
    },
    "questionsFilesFilter.name"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.questionsFilesFilter.slug = "";
      this.filterQuestionFileMultipleFiles();
    },
    "collaboratorFilter.email"(newVal, oldVal) {
      if (newVal == oldVal || oldVal == null) return;
      this.filterCollaborators();
    },
    automationActionToEdit: {
      handler() {
        if (!this.automationActionToEdit) return (this.isInitializing = false);
        this.buildEventAndActionValue();
      },
      immediate: true,
    },
  },
  methods: {
    async handleClear(value) {
      if (!value) {
        setTimeout(() => {
          this.eventEffect.content_id = "";
        }, 50);
      }
    },
    getDefaultTitle(type, info) {
      switch (type) {
        case "ASSIGN_COLLABORATOR": {
          return this.$t("automation.defaultTitle.assign_collaborator", {
            value: info.email,
          });
        }
        case AUTOMATION_EVENT.STATUS_IS_SET:
        case AUTOMATION_EFFECT.SET_STATUS:
          return this.$t("automation.defaultTitle.set_status", {
            value: info.name,
          });
        case AUTOMATION_EFFECT.SEND_OFFER:
          return this.$t("automation.defaultTitle.send_offer", {
            value: info.title,
          });
        case AUTOMATION_EFFECT.SEND_CAMPAIGN:
          return this.$t("automation.defaultTitle.send_offer", {
            value: info.name,
          });
        case AUTOMATION_EFFECT.SEND_FORM:
          return this.$t("automation.defaultTitle.send_form", {
            value: info.title,
          });
        case AUTOMATION_EFFECT.GENERATE_DOC:
          return this.$t("automation.defaultTitle.generate_doc", {
            value: info.name,
          });
        case AUTOMATION_EFFECT.ARCHIVE_FOLDER:
          return this.$t("automation.defaultTitle.archive_folder");
        case AUTOMATION_EFFECT.SEND_TO_GED:
          return this.$t("automation.defaultTitle.send_to_ged");
        default:
          return null;
      }
    },
    reset() {
      if (this.isSubmiting) return;
      this.$emit("close");
    },
    async buildEventAndActionValue() {
      this.isInitializing = true;
      // init value
      this.eventEffect = {
        activate_in: this.automationActionToEdit.activateIn,
        action: this.automationActionToEdit.event,
      };
      // fetching corresponding value
      await this.handleEventChange();

      // building content_id
      this.eventEffect.content_id = this.getContentId(
        this.eventEffect.action,
        this.automationActionToEdit.eventExtra
      );

      this.isInitializing = false;
    },
    async fetchCollaborators() {
      if (
        this.collaboratorFilter.loading &&
        !this.collaboratorFilter.page &&
        !this.organizationId
      )
        return;
      this.collaboratorFilter.loading = true;
      const res = await OrganizationAPI.getOrganizationCollaborators(
        this.organizationId,
        this.collaboratorFilter.page,
        this.collaboratorFilter.email
      );
      this.collaboratorFilter = {
        ...this.collaboratorFilter,
        items: [...res.data.results],
        next: res.data.next,
      };
      this.collaboratorFilter.loading = false;
    },
    filterCollaborators() {
      this.collaboratorFilter.page = 1;
      if (this.collaboratorFilter.searchTimeoutRef)
        clearTimeout(this.collaboratorFilter.searchTimeoutRef);
      this.collaboratorFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchCollaborators();
      }, 200);
    },
    async loadMoreCollaborators(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.collaboratorFilter.loading ||
        !this.collaboratorFilter.next
      )
        return;
      this.collaboratorFilter.page = this.collaboratorFilter.next;
      this.collaboratorFilter.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationCollaborators(
          this.organizationId,
          this.collaboratorFilter.page,
          this.collaboratorFilter.email
        );
        this.collaboratorFilter = {
          items: [...this.collaboratorFilter.items, ...res.data.results],
          next: res.data.next,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },

    // document templates

    async fetchOrganizationDocumentTemplates() {
      if (this.eventDocumentTemplates.loading) return;
      this.eventDocumentTemplates.loading = true;
      const params = {
        page: this.eventDocumentTemplates.page,
        pageSize: this.eventDocumentTemplates.pageSize,
      };
      if (this.eventDocumentTemplates.name)
        params.name = this.eventDocumentTemplates.name;
      const res = await OrganizationAPI.getOrganizationDocumentTemplates(
        this.organizationId,
        params
      );
      this.eventDocumentTemplates = {
        ...this.eventDocumentTemplates,
        items: [...res.data.results],
        next: res.data.next,
      };
      this.eventDocumentTemplates.loading = false;
    },
    filterDocumentTemplates() {
      this.eventDocumentTemplates.page = 1;
      if (this.eventDocumentTemplates.searchTimeoutRef)
        clearTimeout(this.eventDocumentTemplates.searchTimeoutRef);
      this.eventDocumentTemplates.searchTimeoutRef = setTimeout(async () => {
        this.fetchOrganizationDocumentTemplates();
      }, 200);
    },
    async loadMoreDocumentTemplates(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.eventDocumentTemplates.loading ||
        !this.eventDocumentTemplates.next
      )
        return;
      this.eventDocumentTemplates.page = this.eventDocumentTemplates.next;
      this.eventDocumentTemplates.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationDocumentTemplates(
          this.organizationId,
          {
            page: this.eventDocumentTemplates.page,
            name: this.eventDocumentTemplates.name,
            pageSize: this.eventDocumentTemplates.pageSize,
          }
        );
        this.eventDocumentTemplates = {
          items: [...this.eventDocumentTemplates.items, ...res.data.results],
          next: res.data.next,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.eventDocumentTemplates.loading = false;
    },
    getContentId(type, info) {
      switch (type) {
        case AUTOMATION_EVENT.STATUS_IS_SET:
        case AUTOMATION_EFFECT.SET_STATUS:
          return [...this.workflowStatuses].find(
            (status) => status.name === info.name
          )?.id;
        case AUTOMATION_EFFECT.SEND_OFFER:
          return [...this.offerTemplates.items].find(
            (offerTemplate) => offerTemplate.title === info.title
          )?.id;
        case AUTOMATION_EFFECT.GENERATE_DOC:
          return [...this.documentTemplates].find(
            (documentTemplate) => documentTemplate.name === info.name
          )?.id;
        case AUTOMATION_EVENT.DOC_GENERATED:
          return [...this.eventDocumentTemplates.items].find(
            (doc) => doc.name === info.name
          )?.id;
        case AUTOMATION_EVENT.WIZIDEE_CONTROL_FAILED:
          return [...this.validationSchema.items].find(
            (validation) => validation.name === info.name
          )?.id;
        case AUTOMATION_EFFECT.SEND_CAMPAIGN:
        case AUTOMATION_EVENT.CAMPAIGN_ANSWERED: {
          const value = this.effectCampaigns.items.length
            ? [...this.effectCampaigns.items]
            : [...this.eventCampaigns.items];
          return value.find((campaign) => campaign.name === info.name)?.id;
        }
        case AUTOMATION_EVENT.FORM_ANSWERED: {
          const value = this.eventForms.items;
          return info.name
            ? value.find((form) => form.title === info.name)?.id
            : "";
        }
        default:
          return null;
      }
    },
    getActionConfigId(type, config) {
      switch (type) {
        case AUTOMATION_EFFECT.SEND_TO_GED:
          return config.xmlPattern;
        case AUTOMATION_EFFECT.GENERATE_DOC:
          return [...this.questionFiles.items].find(
            (question) => question.slug === config.destination
          )?.slug;
        default:
          return null;
      }
    },
    async fetchCosmoformTemplate() {
      this.eventForms.loading = true;
      try {
        this.eventForms.page = 1;
        const filters = {
          page: this.eventForms.page,
          pageSize: this.eventForms.pageSize,
        };
        if (this.eventForms.name) filters.name = this.eventForms.name;
        const { item, count } = await getOrganizationWorkflowCosmoformTemplate({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          filters,
        });
        this.eventForms = {
          ...this.eventForms,
          items: item,
          count: count,
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.eventForms.loading = false;
    },
    filterCosmoformTemplate() {
      if (this.eventForms.searchTimeoutRef)
        clearTimeout(this.eventForms.searchTimeoutRef);
      this.eventForms.searchTimeoutRef = setTimeout(async () => {
        this.fetchCosmoformTemplate();
      }, 200);
    },

    async fetchQuestionFileMultipleFile() {
      this.questionsFilesFilter.loading = true;
      try {
        this.questionsFilesFilter.page = 1;
        this.questionFiles = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.questionsFilesFilter.page,
          name: this.questionsFilesFilter.name,
          slug: this.questionsFilesFilter.slug,
          pageSize: this.questionsFilesFilter.pageSize,
          typeQuestion: this.questionsFilesFilter.typeQuestion,
        });
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },
    filterQuestionFileMultipleFiles() {
      if (this.questionsFilesFilter.searchTimeoutRef)
        clearTimeout(this.questionsFilesFilter.searchTimeoutRef);
      this.questionsFilesFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchQuestionFileMultipleFile();
      }, 200);
    },
    async loadMoreQuestionTemplates(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.questionsFilesFilter.loading ||
        !this.questionFiles.next
      )
        return;
      this.questionsFilesFilter.page = this.questionFiles.next;
      this.questionsFilesFilter.loading = true;
      try {
        const tmp = await getWorkflowQuestions({
          organizationId: this.$route.params.organizationId,
          workflowId: this.$route.params.workflowId,
          page: this.questionsFilesFilter.page,
          slug: this.questionsFilesFilter.slug,
          pageSize: this.questionsFilesFilter.pageSize,
        });
        this.questionFiles = {
          ...tmp,
          items: [...this.questionFiles.items, ...tmp.items],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.questionsFilesFilter.loading = false;
    },

    async fecthOfferTemplates() {
      this.fetchingOfferTemplate = true;
      const response = await OrganizationAPI.getOrganizationOfferTemplate({
        organizationId: this.organizationId,
        params: {
          page: 1,
          offer_type: ["CONSULT", "VALIDATION"],
        },
      });
      this.offerTemplates = {
        ...response.data,
        items: response.data.results,
      };
      this.fetchingOfferTemplate = false;
    },
    async fetchCosmoformTemplates() {
      this.fetchingCosmoformTemplate = true;
      this.cosmoformTemplates = (
        await getOrganizationCosmoformTemplate({
          organizationId: this.$route.params.organizationId,
        })
      )?.item;
      this.fetchingCosmoformTemplate = false;
    },
    async fetchEventCampaigns() {
      this.eventCampaignFilter.loading = true;
      try {
        this.eventCampaignFilter.page = 1;
        const res = await getOrganizationCampaigns({
          organizationId: this.organizationId,
          filters: this.eventCampaignFilter,
        });
        this.eventCampaigns = {
          ...res.data,
          items: [...res.data.results],
        };
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.eventCampaignFilter.loading = false;
    },
    filterCampaigns() {
      if (this.eventCampaignFilter.searchTimeoutRef)
        clearTimeout(this.eventCampaignFilter.searchTimeoutRef);
      this.eventCampaignFilter.searchTimeoutRef = setTimeout(async () => {
        this.fetchEventCampaigns();
      }, 500);
    },
    async fetchDocumentTemplates() {
      this.fetchingDocumentTemplates = true;
      try {
        const res = await OrganizationAPI.getOrganizationDocumentTemplates(
          this.organizationId
        );
        this.documentTemplates = res.data.results;
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }

      this.fetchingDocumentTemplates = false;
    },

    // to fetch validation schema for WIZIDEE_CONTROL_FAILED event
    async fetchValidationSchema() {
      this.validationSchema.loading = true;
      try {
        const res = await OrganizationAPI.getOrganizationValidationSchema({
          organizationId: this.organizationId,
        });
        this.validationSchema.items = res.data.results;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.validationSchema.loading = false;
    },
    async submit() {
      this.$v.eventEffect.$touch();
      if (this.$v.eventEffect.$invalid) return;
      this.isSubmiting = true;
      try {
        const payload = new FormData();
        payload.append("event", this.eventEffect.action);
        // to exclude Every from form answered all
        if (this.eventEffect.content_id && this.eventEffect.content_id !== "")
          payload.append("event_content_id", this.eventEffect.content_id);
        else if (this.eventEffect.activate_in)
          payload.append("activate_in", this.eventEffect.activate_in);
        if (this.automationActionToEdit) {
          const response = await WorkflowAPI.patchOrganizationWorkflowEffet({
            organizationId: this.organizationId,
            workflowId: this.workflow.id,
            effectId: this.automationActionToEdit.id,
            payload: payload,
          });
          this.$emit("effect-edited", { value: response.data });
        }
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.snackbar.success.workflowModifiedSuccessfully"
          ),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("workflow.snackbar.error.errorModifyingWorkflow"),
          type: "ERROR",
        });
      }
      this.isSubmiting = false;
      this.reset();
    },
    removeSelectedEffect(item) {
      this.eventEffect.content_id = this.eventEffect.content_id.filter(
        (tmp) => tmp != item.id
      );
    },
    async handleEventChange() {
      this.eventEffect.content_id = null;
      switch (this.eventEffect.action) {
        case AUTOMATION_EVENT.CAMPAIGN_ANSWERED:
          await this.fetchEventCampaigns();
          break;
        case AUTOMATION_EVENT.DOC_GENERATED:
          await this.fetchOrganizationDocumentTemplates();

          setTimeout(() => {
            this.loaded = true;
          }, 100);
          break;
        case AUTOMATION_EVENT.FORM_ANSWERED:
          this.firstInitialization = false;
          await this.fetchCosmoformTemplate();
          this.eventEffect.content_id = "";
          this.firstInitialization = true;
          break;
        case AUTOMATION_EVENT.WIZIDEE_CONTROL_FAILED:
          if (!this.validationSchema.items.length)
            await this.fetchValidationSchema();
          break;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: 8px;
  flex-wrap: wrap;
  .modal_title {
    font-weight: 600;
    font-size: 24px;
    line-height: 32px;
    color: #353542;
  }
  .close_icon {
    margin-left: auto;
  }
}
.modal_content {
  position: relative;

  .input_group {
    display: flex;
    flex-direction: column;
    gap: 16px;
    .group_label {
      flex: none;
      width: 80px;
      height: 42px;
      display: flex;
      align-items: center;
      font-family: Inter;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: #353542;
    }
    .input_section {
      width: 100%;
      flex: none;
      display: flex;
      gap: 16px;
      .action_type {
        display: flex;
        align-items: center;
        gap: 4px;
        border-bottom: 1px solid #353542;
        height: 80px;
        width: 100%;
        .action_label {
          text-decoration: underline;
          font-weight: bold;
          font-size: 16px;
          color: #353542;
        }
      }
      .action_section {
        display: flex;
        flex-direction: column;
      }
      & > * {
        flex: 1;
      }
      .inputs {
        display: flex;
        gap: 16px;
        & > * {
          width: calc(50% - 8px);
          flex: none;
        }
      }
    }
  }
  .advanced_section {
    .advanced_header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      cursor: pointer;
      .chevron_btn {
        width: 24px;
        flex: none;
        transition: all 0.3s ease-out;
        &.expanded {
          transform: rotate(90deg);
        }
      }
      .header_label {
        flex: none;
        width: 160px;
        align-items: center;
        font-family: Inter;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        color: #353542;
      }
    }
    .advanced_content {
      .advanced_input {
        display: flex;
        .input_label {
          width: 176px;
          height: 42px;
          display: flex;
          align-items: center;
          color: #353542;
          font-family: Inter;
          font-size: 14px;
          line-height: 18px;
          font-style: normal;
          font-weight: 400;
        }
        .input_section {
          width: calc(100% - 176px);
          flex: none;
        }
      }
      .checkbox_item {
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        label {
          width: 176px;
          color: #353542;
          font-family: Inter;
          font-size: 14px;
          line-height: 18px;
          font-style: normal;
          font-weight: 400;
        }
        input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      }
    }
  }
  .progression {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 10;
  }
}
.modal_footer {
  display: flex;
  justify-content: flex-end;
  align-content: center;
  padding: 8px 0;
  gap: 16px;
}
.mail_chips {
  background: rgba(155, 189, 255, 0.2) !important;
  color: #2536cc !important;
  border-radius: 21px;
  ::v-deep .v-chip__close {
    color: #2536cc;
  }
}
.notification_skeleton {
  height: 40px;
  width: 100%;
  ::v-deep {
    .v-skeleton-loader__bone {
      height: 40px;
    }
  }
}
.skeleton {
  height: 42px;
  width: 100%;
  background: transparent;
  ::v-deep {
    .v-skeleton-loader__bone {
      height: 42px;
    }
  }
}
</style>
